import React, { useState, useEffect } from 'react';
import '../../styles/orders.css';
import api from '../../services/api';
import { useLanguage } from '../../utils/LanguageContext';
import { getLocalized } from '../../utils/languageUtils';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../routes/path';

const Orders = () => {
  const { t, language } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, completed

  useEffect(() => {
    api.get('/get/my/ordered/trees/')
      .then(res => {
        setOrders(res.data || []);
      })
      .catch(err => {
        console.error('Error fetching orders:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Determine order status based on data
  const getOrderStatus = (item) => {
    // If the order has been planted, it's completed
    if (item.is_planted || item.status === 'planted') return 'planted';
    if (item.status === 'processing') return 'processing';
    return 'ordered';
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'planted': return t('orders_status_planted') || 'Planted';
      case 'processing': return t('orders_status_processing') || 'Processing';
      default: return t('orders_status_ordered') || 'Ordered';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'planted': return 'status-planted';
      case 'processing': return 'status-processing';
      default: return 'status-ordered';
    }
  };
  const filteredOrders = orders.filter(item => {
    if (filter === 'all') return true;
    const status = getOrderStatus(item);
    if (filter === 'active') return status === 'ordered' || status === 'processing';
    if (filter === 'completed') return status === 'planted';
    return true;
  });

  const activeCount = orders.filter(o => {
    const s = getOrderStatus(o);
    return s === 'ordered' || s === 'processing';
  }).length;
  
  const completedCount = orders.filter(o => getOrderStatus(o) === 'planted').length;
  const totalTreeCount = orders.reduce((sum, o) => sum + (o.count || 1), 0);
  if (loading) {
    return (
      <div className="orders-container">
        <div className="orders-loading">
          <div className="loading-spinner"></div>
          <p>{t('orders_loading') || 'Loading your orders...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-container">
      {/* Page Header */}
      <div className="orders-header">
        <div className="orders-header-content">
          <div className="orders-header-text">
            <h1><i className="fa-solid fa-receipt"></i> {t('orders_title') || 'My Orders'}</h1>
            <p>{t('orders_subtitle') || 'Track your tree orders and planting progress'}</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="orders-stats">
          <div className="order-stat-card">
            <div className="order-stat-icon">
              <i className="fa-solid fa-clipboard-list"></i>
            </div>
            <div className="order-stat-info">
              <span className="order-stat-value">{orders.length}</span>
              <span className="order-stat-label">{t('orders_total') || 'Total Orders'}</span>
            </div>
          </div>
          <div className="order-stat-card highlight">
            <div className="order-stat-icon">
              <i className="fa-solid fa-seedling"></i>
            </div>
            <div className="order-stat-info">
              <span className="order-stat-value">{totalTreeCount}</span>
              <span className="order-stat-label">{t('orders_trees_ordered') || 'Trees Ordered'}</span>
            </div>
          </div>
          <div className="order-stat-card">
            <div className="order-stat-icon active-icon">
              <i className="fa-solid fa-clock"></i>
            </div>
            <div className="order-stat-info">
              <span className="order-stat-value">{activeCount}</span>
              <span className="order-stat-label">{t('orders_active') || 'Active'}</span>
            </div>
          </div>
          <div className="order-stat-card">
            <div className="order-stat-icon completed-icon">
              <i className="fa-solid fa-check-circle"></i>
            </div>
            <div className="order-stat-info">
              <span className="order-stat-value">{completedCount}</span>
              <span className="order-stat-label">{t('orders_completed') || 'Completed'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="orders-filters">
        <button
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          <i className="fa-solid fa-layer-group"></i> {t('orders_filter_all') || 'All Orders'}
          <span className="filter-count">{orders.length}</span>
        </button>
        <button
          className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          <i className="fa-solid fa-spinner"></i> {t('orders_filter_active') || 'Active'}
          <span className="filter-count">{activeCount}</span>
        </button>
        <button
          className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          <i className="fa-solid fa-circle-check"></i> {t('orders_filter_completed') || 'Completed'}
          <span className="filter-count">{completedCount}</span>
        </button>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="orders-empty">
          <div className="empty-icon">
            <i className="fa-solid fa-box-open"></i>
          </div>
          <h3>{filter === 'all'
            ? (t('orders_empty_title') || 'No orders yet')
            : (t('orders_empty_filter') || 'No orders match this filter')
          }</h3>
          <p>{t('orders_empty_desc') || 'Visit the shop to order your first trees!'}</p>
          <Link to={`/${ROUTES.SHOP}`} className="btn-shop-now">
            <i className="fa-solid fa-store"></i> {t('orders_go_shop') || 'Go to Shop'}
          </Link>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map(item => {
            const prod = item.product || {};
            const tree = prod.tree || {};
            const status = getOrderStatus(item);
            const treeImage = tree.image || null;
            const price = parseFloat(prod.price) || 0;
            const discount = parseFloat(prod.discount) || 0;
            const subtotal = price * (item.count || 1);
            const discountAmount = (subtotal * discount) / 100;
            const total = subtotal - discountAmount;

            return (
              <div key={item.id} className={`order-card ${status}`}>
                {/* Order Header */}
                <div className="order-card-header">
                  <div className="order-id-section">
                    <span className="order-id-label">{t('orders_order_id') || 'Order'} #{item.id}</span>
                    <span className={`order-status-badge ${getStatusClass(status)}`}>
                      <i className={`fa-solid ${status === 'planted' ? 'fa-check' : status === 'processing' ? 'fa-gear fa-spin' : 'fa-clock'}`}></i>
                      {getStatusLabel(status)}
                    </span>
                  </div>
                  {item.created_at && (
                    <span className="order-date">
                      <i className="fa-regular fa-calendar"></i>
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Order Body */}
                <div className="order-card-body">
                  <div className="order-tree-image">
                    {treeImage ? (
                      <img src={treeImage} alt={getLocalized(tree, language, 'name')} />
                    ) : (
                      <div className="tree-image-placeholder">
                        <i className="fa-solid fa-tree"></i>
                      </div>
                    )}
                  </div>

                  <div className="order-details">
                    <h3 className="order-tree-name">
                      {getLocalized(tree, language, 'name') || 'Tree'}
                    </h3>
                    <p className="order-tree-family">{tree.family?.name || ''}</p>

                    <div className="order-meta-row">
                      <div className="order-meta-item">
                        <i className="fa-solid fa-cubes"></i>
                        <span>{t('orders_quantity') || 'Qty'}: <strong>{item.count || 1}</strong></span>
                      </div>
                    </div>
                  </div>

                  <div className="order-price-section">
                    <span className="order-total-price">{total.toLocaleString()} UZS</span>
                    {discount > 0 && (
                      <span className="order-original-price">{subtotal.toLocaleString()} UZS</span>
                    )}
                  </div>
                </div>

                {/* Status Timeline */}
                <div className="order-timeline">
                  <div className={`timeline-step ${status === 'ordered' || status === 'processing' || status === 'planted' ? 'done' : ''}`}>
                    <div className="timeline-dot"><i className="fa-solid fa-shopping-cart"></i></div>
                    <span>{t('orders_step_ordered') || 'Ordered'}</span>
                  </div>
                  <div className="timeline-line"></div>
                  <div className={`timeline-step ${status === 'processing' || status === 'planted' ? 'done' : ''}`}>
                    <div className="timeline-dot"><i className="fa-solid fa-gear"></i></div>
                    <span>{t('orders_step_processing') || 'Processing'}</span>
                  </div>
                  <div className="timeline-line"></div>
                  <div className={`timeline-step ${status === 'planted' ? 'done' : ''}`}>
                    <div className="timeline-dot"><i className="fa-solid fa-tree"></i></div>
                    <span>{t('orders_step_planted') || 'Planted'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
